/**
 * Current traffic-light level for the always-visible banner (docs/08 §7).
 *
 * `GET /api/traffic/current` → `{ level, since, ... }`. Until the traffic module answers (501 or
 * 404) the level is VERDE with no error, so the shell never shows an alarm it cannot justify.
 * The key starts with `traffic`: SSE `invalidate` events refresh it.
 */
import type { IsoDateTime, TrafficLevel } from 'core';
import { TRAFFIC_LEVELS } from 'core';
import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from './api';

export interface CurrentTraffic {
  level: TrafficLevel;
  since: IsoDateTime | null;
  /** False when the API does not provide the level yet (VERDE by default). */
  available: boolean;
}

export const DEFAULT_TRAFFIC: CurrentTraffic = { level: 'VERDE', since: null, available: false };

/** Normalises the answer (`TrafficStatus` or `{level, since, event}`). */
export function parseCurrentTraffic(payload: unknown): CurrentTraffic {
  if (typeof payload !== 'object' || payload === null) return DEFAULT_TRAFFIC;
  const p = payload as { level?: unknown; since?: unknown };
  const level = TRAFFIC_LEVELS.find((l) => l === p.level);
  if (!level) return DEFAULT_TRAFFIC;
  return { level, since: typeof p.since === 'string' ? p.since : null, available: true };
}

export async function fetchCurrentTraffic(): Promise<CurrentTraffic> {
  try {
    return parseCurrentTraffic(await api.get<unknown>('/traffic/current'));
  } catch (err) {
    if (err instanceof ApiError && (err.status === 501 || err.status === 404)) {
      return DEFAULT_TRAFFIC;
    }
    throw err;
  }
}

export const currentTrafficQuery = {
  queryKey: ['traffic', 'current'] as const,
  queryFn: fetchCurrentTraffic,
  staleTime: 10_000,
  refetchInterval: 60_000,
};

export function useCurrentTraffic() {
  return useQuery(currentTrafficQuery);
}
